"use client";

import { DatePicker } from "~/components/ui/datepicker"; // Adjust paths as needed
import { TimePicker } from "~/components/ui/timepicker"; // Adjust paths as needed
import {
  existingSlotOptions,
  MAX_DURATION_MS,
  pad,
  parseLocalISO,
  slotValue,
  toLocalISO,
  wallClockNowIn,
} from "~/lib/utils/datetime";

// ── Helpers ───────────────────────────────────────────────────────────────────

/** `base`'s clock time moved onto `day`'s calendar date. */
function withDate(base: Date, day: Date): string {
  return toLocalISO(
    new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      base.getHours(),
      base.getMinutes(),
    ),
  );
}

/** `base`'s calendar date at the "HH:MM" slot `timeVal`. */
function withTime(base: Date, timeVal: string): string {
  const [h, m] = timeVal.split(":").map(Number);
  return toLocalISO(
    new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      h ?? 0,
      m ?? 0,
    ),
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface DateTimeFieldsProps {
  startsAt: string;
  endsAt: string;
  onStartsAtChange: (v: string) => void;
  onEndsAtChange: (v: string) => void;
  now: Date;
  /**
   * The destination's IANA zone (see `timeZoneForCoords`) — the clock these
   * fields are picking on.
   *
   * `startsAt`/`endsAt` are bare wall clocks with no zone of their own, so this
   * decides two things: which day counts as "today" for the calendar's lower
   * bound, and which times on that day have already passed. A driver in
   * California searching New York at 8 AM their time is at 11 AM the
   * destination's, so 8–11 AM must not be offerable — those slots are in the
   * past where the car would actually be parked.
   *
   * Null/omitted keeps the viewer's clock, which is what these forms used
   * before a destination is chosen and when a coordinate can't be placed.
   */
  timeZone?: string | null;
  initialActiveCalendar?: "start" | "end" | null;
}

// ── DateTimeFields ────────────────────────────────────────────────────────────
export function DateTimeFields({
  startsAt,
  endsAt,
  onStartsAtChange,
  onEndsAtChange,
  now,
  timeZone,
  initialActiveCalendar = null,
}: DateTimeFieldsProps) {
  const startDate = parseLocalISO(startsAt);
  const endDate = parseLocalISO(endsAt);

  const startTimeValue = startDate
    ? `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}`
    : "09:00";
  const endTimeValue = endDate
    ? `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`
    : "10:00";

  // "Today" and "now" on the clock these fields are picking on — the
  // destination's when one is set, the viewer's before that. The pickers below
  // compare bare calendar days and times-of-day, so both bounds have to be read
  // in the same frame the times are chosen in; otherwise the calendar offers a
  // day already over at the destination, or withholds one that has started there.
  const nowLocal = wallClockNowIn(now, timeZone);
  const todayMidnight = new Date(
    nowLocal.getFullYear(),
    nowLocal.getMonth(),
    nowLocal.getDate(),
  );
  const maxDate = new Date(
    nowLocal.getFullYear() + 1,
    nowLocal.getMonth(),
    nowLocal.getDate(),
  );

  const startMidnight = startDate
    ? new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
      )
    : null;
  const endMidnight = endDate
    ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
    : null;

  // The latest end `resolveSearchWindow` will accept, measured from the start rather
  // than from today: the clamp is on the span, so a calendar-year ceiling let the
  // picker offer ends it then silently pulled back — by an hour on the last day, or a
  // whole day across a leap year. Read off the instant, so DST is accounted for too.
  const endCeiling = new Date(
    (startDate ?? nowLocal).getTime() + MAX_DURATION_MS,
  );
  const endCeilingMidnight = new Date(
    endCeiling.getFullYear(),
    endCeiling.getMonth(),
    endCeiling.getDate(),
  );

  const isRange = !!(
    startMidnight &&
    endMidnight &&
    startMidnight.getTime() < endMidnight.getTime()
  );

  const isSameDay =
    startDate &&
    endDate &&
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  // ── Calculate Past Time Limits ──────────────────────────────────────────────
  const isStartToday = startMidnight?.getTime() === todayMidnight.getTime();

  // The slot `now` is inside stays offered: at 2:44 the list starts at 2:30.
  // Floored on the clock above, so a driver in California is not offered 8:00 AM
  // for a New York spot where it is already 11:00 AM — those slots name times
  // that have passed at the place the car goes.
  const currentMinTimeVal = slotValue(nowLocal);

  // The slot table is the same 96 times every day, but a day the destination
  // spends an hour of in a DST gap does not have all 96 — on 2026-03-08 in New
  // York the clock goes 02:00 → 03:00, so 02:00–02:45 name nothing. Struck per
  // day and per zone before the bounds below narrow further, so a filtered slot
  // can't come back through the floor or the cap.
  const startSlots = existingSlotOptions(startDate ?? nowLocal, timeZone);
  const endSlots = existingSlotOptions(endDate ?? nowLocal, timeZone);

  // Filter start times if the selected date is today
  const startTimeOptions = isStartToday
    ? startSlots.filter((opt) => opt.value >= currentMinTimeVal)
    : startSlots;

  // Same-day ends must come after the start. A later day needs no floor: the
  // start is never in the past, so an end on a later day never is either. On the
  // ceiling day the times are capped instead, or the last few slots would each be
  // over the limit by the amount they exceed the start's time of day.
  const isEndOnCeilingDay =
    endMidnight?.getTime() === endCeilingMidnight.getTime();
  const endTimeOptions = isSameDay
    ? endSlots.filter((opt) => opt.value > startTimeValue)
    : isEndOnCeilingDay
      ? endSlots.filter((opt) => opt.value <= slotValue(endCeiling))
      : endSlots;

  // No effect repairs a stale value here: the owner re-resolves the pair when the
  // clock crosses a slot, before this renders. These fields stay presentational.

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleStartDateChange(day: Date) {
    onStartsAtChange(withDate(startDate ?? nowLocal, day));
  }

  function handleEndDateChange(day: Date) {
    onEndsAtChange(withDate(endDate ?? nowLocal, day));
  }

  function handleStartTimeChange(timeVal: string) {
    onStartsAtChange(withTime(startDate ?? nowLocal, timeVal));
  }

  function handleEndTimeChange(timeVal: string) {
    onEndsAtChange(withTime(endDate ?? nowLocal, timeVal));
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* ── START TIME ──────────────────────────────────────────────────────── */}
      <div>
        <p className="mb-2 block text-[13px] font-medium text-[var(--blue)]">
          Start Time
        </p>
        <div className="grid grid-cols-2 gap-2">
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            defaultOpen={initialActiveCalendar === "start"}
            disabled={(d) => d < todayMidnight || d > maxDate}
            // Bounds the month arrows to the same range `disabled` greys out.
            // Without these the calendar pages forward forever into months where
            // every day is dead.
            startMonth={todayMidnight}
            endMonth={maxDate}
            rangeStart={startMidnight}
            rangeEnd={endMidnight}
            isRange={isRange}
          />
          <TimePicker
            value={startTimeValue}
            onChange={handleStartTimeChange}
            options={startTimeOptions}
            className="w-full"
          />
        </div>
      </div>

      {/* ── END TIME ────────────────────────────────────────────────────────── */}
      <div>
        <p className="mb-2 block text-[13px] font-medium text-[var(--blue)]">
          End Time
        </p>
        <div className="grid grid-cols-2 gap-2">
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            defaultOpen={initialActiveCalendar === "end"}
            disabled={(d) => {
              const startMid = startMidnight ?? todayMidnight;
              const dMid = new Date(d.getFullYear(), d.getMonth(), d.getDate());
              // Never before the start's day. This used to exempt today
              // unconditionally, so today stayed clickable with a future start.
              return dMid < startMid || dMid > endCeilingMidnight;
            }}
            // Opens on the start's month and stops where the clamp does.
            startMonth={startMidnight ?? todayMidnight}
            endMonth={endCeilingMidnight}
            rangeStart={startMidnight}
            rangeEnd={endMidnight}
            isRange={isRange}
          />
          <TimePicker
            value={endTimeValue}
            onChange={handleEndTimeChange}
            options={endTimeOptions}
            className="w-full"
            referenceTime={isSameDay ? startTimeValue : undefined}
          />
        </div>
      </div>
    </div>
  );
}
